import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ListGroup } from 'react-bootstrap';

/** Tallest the list grows before it scrolls. */
const LIST_MAX_HEIGHT = 240;

/** Where the open list sits on screen, measured from the box. */
interface ListPlacement {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
}

interface RolePickerProps {
  /** The roles that may be picked, in the order shown. */
  options: readonly string[];
  /** The picked role, or '' when none is picked. */
  value: string;
  onChange: (role: string) => void;
  ariaLabel: string;
  placeholder: string;
  size?: 'sm';
  style?: CSSProperties;
}

/**
 * Looks and opens like a select, but the box takes typing: "marketing" narrows the list to every role containing it.
 * Only a role from the list is ever reported; text that matches none is dropped when the box loses focus. The list is
 * drawn on top of the page, not inside the box's container, so a short or scrolling table cannot cut it off.
 */
const RolePicker = ({ options, value, onChange, ariaLabel, placeholder, size, style }: RolePickerProps) => {
  const listId = useId();
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const boxRef = useRef<HTMLInputElement>(null);
  const [placement, setPlacement] = useState<ListPlacement | null>(null);

  // Opens below the box, or above it when the window has more room there; follows the box on scroll and resize.
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const place = () => {
      const box = boxRef.current?.getBoundingClientRect();
      if (!box) {
        return;
      }
      const roomBelow = window.innerHeight - box.bottom;
      const opensUp = roomBelow < LIST_MAX_HEIGHT && box.top > roomBelow;
      setPlacement({
        left: box.left,
        width: Math.max(box.width, 220),
        ...(opensUp ? { bottom: window.innerHeight - box.top } : { top: box.bottom }),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // A pick cleared from outside (after Give or Add person) empties the box too.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const term = query.trim().toLowerCase();
  const showsPick = value !== '' && query === value;
  const shown = showsPick || term === '' ? options : options.filter((role) => role.toLowerCase().includes(term));

  const close = () => {
    setOpen(false);
    setQuery(value);
  };

  const pick = (role: string) => {
    onChange(role);
    setQuery(role);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlighted((current) => (shown.length === 0 ? 0 : (current + step + shown.length) % shown.length));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      if (shown[highlighted]) {
        pick(shown[highlighted]);
      }
    } else if (event.key === 'Escape') {
      close();
    }
  };

  return (
    <div style={style}>
      <input
        ref={boxRef}
        className={`form-select${size === 'sm' ? ' form-select-sm' : ''}`}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        placeholder={placeholder}
        value={query}
        onFocus={(event) => {
          setOpen(true);
          event.target.select();
        }}
        onClick={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setHighlighted(0);
          setOpen(true);
          if (value !== '') {
            onChange('');
          }
        }}
        onKeyDown={onKeyDown}
        onBlur={close}
      />
      {open &&
        createPortal(
          <ListGroup
            id={listId}
            role="listbox"
            style={{
              position: 'fixed',
              zIndex: 1060,
              ...placement,
              maxHeight: LIST_MAX_HEIGHT,
              overflowY: 'auto',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            {shown.length === 0 ? (
              <ListGroup.Item className="text-muted small">{`No role matches "${query.trim()}"`}</ListGroup.Item>
            ) : (
              shown.map((role, index) => (
                <ListGroup.Item
                  key={role}
                  role="option"
                  aria-selected={role === value}
                  action
                  active={index === highlighted}
                  className={size === 'sm' ? 'py-1 small' : undefined}
                  // mousedown: picking must happen before the box loses focus and closes the list. click as well, for
                  // assistive technology that activates an option with a click alone; picking twice is harmless.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pick(role);
                  }}
                  onClick={() => pick(role)}
                  onMouseEnter={() => setHighlighted(index)}
                >
                  {role}
                </ListGroup.Item>
              ))
            )}
          </ListGroup>,
          document.body,
        )}
    </div>
  );
};

export default RolePicker;
